/**
 * Idle detection (STEP 4) — tracks real user activity (mouse, keyboard,
 * scroll, touch) and fires `onIdle` once `timeoutMs` elapses with none.
 * Generic and reusable on its own (not auth-specific); `useSessionTimeout`
 * composes this for the session-timeout use case specifically.
 */
import { useEffect, useRef, useCallback } from 'react';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'wheel'] as const;

export interface UseIdleTimerOptions {
  timeoutMs: number;
  onIdle: () => void;
  enabled?: boolean;
}

export function useIdleTimer({ timeoutMs, onIdle, enabled = true }: UseIdleTimerOptions): { reset: () => void } {
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const reset = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (enabled) {
      timerRef.current = setTimeout(() => onIdleRef.current(), timeoutMs);
    }
  }, [timeoutMs, enabled]);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    reset();
    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, reset, { passive: true });
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, reset);
      }
    };
  }, [enabled, reset]);

  return { reset };
}
